import React from 'react';
import { Card, CardContent } from './ui/card';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';

const PLATFORM_COLORS = {
  Facebook: '#1877F2',
  Instagram: '#E4405F',
  LinkedIn: '#0A66C2',
  TikTok: '#000000',
  YouTube: '#FF0000',
  Impressions: '#1f2a4d',
  Engagements: '#e01e25',
  Views: '#0A66C2',
  Clicks: '#16a34a',
};

const CHART_COLORS = ['#1f2a4d', '#e01e25', '#0A66C2', '#16a34a', '#f59e0b', '#8b5cf6'];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload) return null;
  return (
    <div className="bg-white rounded-xl border shadow-lg p-4 text-xs min-w-[160px]">
      <div className="font-semibold text-[#1f2a4d] mb-2 pb-1.5 border-b">{label}</div>
      {payload.map((item, i) => (
        <div key={i} className="flex items-center justify-between gap-4 py-0.5">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: item.color }} />
            <span className="text-muted-foreground">{item.name}</span>
          </div>
          <span className="font-semibold tabular-nums">{item.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

export default function DashboardChart({ title, data, dataKeys, dateLabels, chartType = 'line', height = 300 }) {
  const chartData = dateLabels?.map((label, idx) => {
    const parts = label.split('-');
    const point = { date: parts.length === 3 ? `${parts[1]}/${parts[2]}` : label };
    dataKeys.forEach(dk => {
      point[dk.name] = data[dk.key]?.[idx] ?? null;
    });
    return point;
  }) || [];

  const renderChart = () => {
    if (chartType === 'area') {
      return (
        <AreaChart data={chartData}>
          <defs>
            {dataKeys.map((dk, i) => {
              const color = PLATFORM_COLORS[dk.name] || CHART_COLORS[i % CHART_COLORS.length];
              return (
                <linearGradient key={dk.key} id={`gradient-${dk.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                </linearGradient>
              );
            })}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e6e8ef" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e6e8ef' }} />
          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={45} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
          {dataKeys.map((dk, i) => {
            const color = PLATFORM_COLORS[dk.name] || CHART_COLORS[i % CHART_COLORS.length];
            return (
              <Area key={dk.key} type="monotone" dataKey={dk.name} stroke={color} strokeWidth={2}
                fill={`url(#gradient-${dk.key})`} activeDot={{ r: 4, strokeWidth: 0 }} />
            );
          })}
        </AreaChart>
      );
    }

    if (chartType === 'bar') {
      return (
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e6e8ef" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e6e8ef' }} />
          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={45} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
          {dataKeys.map((dk, i) => {
            const color = PLATFORM_COLORS[dk.name] || CHART_COLORS[i % CHART_COLORS.length];
            return <Bar key={dk.key} dataKey={dk.name} fill={color} radius={[4, 4, 0, 0]} barSize={20} />;
          })}
        </BarChart>
      );
    }

    // Default: line chart
    return (
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e6e8ef" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e6e8ef' }} />
        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={45} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
        {dataKeys.map((dk, i) => {
          const color = PLATFORM_COLORS[dk.name] || CHART_COLORS[i % CHART_COLORS.length];
          return (
            <Line key={dk.key} type="monotone" dataKey={dk.name} stroke={color} strokeWidth={2.5}
              dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }} />
          );
        })}
      </LineChart>
    );
  };

  return (
    <Card className="rounded-2xl border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)]" data-testid="dashboard-line-chart">
      <CardContent className="p-5 sm:p-6">
        <h3 className="text-sm font-semibold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1f2a4d' }}>
          {title}
        </h3>
        <div className="mt-4">
          <ResponsiveContainer width="100%" height={height}>
            {renderChart()}
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Compact chart for report builder
export function ReportChart({ title, data, dataKeys, dateLabels }) {
  const chartData = dateLabels?.map((label, idx) => {
    const parts = label.split('-');
    const point = { date: parts.length === 3 ? `${parts[1]}/${parts[2]}` : label };
    dataKeys.forEach(dk => {
      point[dk.name] = data[dk.key]?.[idx] ?? null;
    });
    return point;
  }) || [];

  return (
    <div className="rounded-xl border bg-white p-4 mb-4">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{title}</h4>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData}>
          <defs>
            {dataKeys.map((dk, i) => {
              const color = PLATFORM_COLORS[dk.name] || CHART_COLORS[i % CHART_COLORS.length];
              return (
                <linearGradient key={dk.key} id={`rpt-gradient-${dk.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              );
            })}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#aaa' }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#aaa' }} tickLine={false} axisLine={false} width={35} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 10, paddingTop: 6 }} iconType="circle" iconSize={8} />
          {dataKeys.map((dk, i) => {
            const color = PLATFORM_COLORS[dk.name] || CHART_COLORS[i % CHART_COLORS.length];
            return (
              <Area key={dk.key} type="monotone" dataKey={dk.name} stroke={color} strokeWidth={2}
                fill={`url(#rpt-gradient-${dk.key})`} dot={false} />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
